import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useQuill } from '../../editor/QuillContext';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import useElementSizeSelector from '../../editor/utils/useElementSizeSelector';
import { useAllUsersMap } from '../../user/useAllUsers';
import * as styles from './VersionChangesWall.module.scss';
import VersionChangesWallAuthorCard, {
  AuthorCardSizes,
} from './VersionChangesWallAuthorCard';
import type { VersionChange } from './VersionChanges';
import { gql } from '../../graphql/generated/gql';
import type {
  UseAllUsers_UserFragment,
  VersionChangesWallAuthorCard_HistoryFragment,
} from '../../graphql/generated/graphql';

const authorCardGap = parseInt(styles.authorCardGap, 10);

const HISTORY_VERSIONS_QUERY = gql(/** @complexity 3 */ `
  query HistoryVersion($id: ID!) {
    post(id: $id) {
      id
      ...VersionChangesWallAuthorCard_post
      histories {
        id
        versionFrom
        versionTo
        ...VersionChangesWallAuthorCard_history
      }
    }
  }
`);

interface WallItem {
  versionChange: VersionChange;
  author: UseAllUsers_UserFragment;
  top: number;
  history: VersionChangesWallAuthorCard_HistoryFragment;
}

interface VersionChangesWallProps {
  postId: string;
  versionFrom: number;
  versionTo: number;
  onFocus(id: string): void;
  focusedId: string | null;
  versionChanges: VersionChange[];
}

const VersionChangesWall = ({
  postId,
  versionFrom,
  versionTo,
  versionChanges,
  focusedId,
  onFocus,
}: VersionChangesWallProps) => {
  const { quill } = useQuill();
  const usersMap = useAllUsersMap();
  const [wallItems, setWallItems] = useState<WallItem[]>([]);
  const { data: { post } = {} } = useQuery(HISTORY_VERSIONS_QUERY, {
    variables: { id: postId },
  });

  const [setElement, isCompact] = useElementSizeSelector(
    ({ width }) => width < AuthorCardSizes.normalWidth + authorCardGap,
  );

  const authorCardHeight =
    AuthorCardSizes.heights[isCompact ? 'compact' : 'normal'];

  const updateWallItems = useCallback(() => {
    if (!quill || !post?.histories) return;

    const containerTop = quill.root.getBoundingClientRect().top;
    const newWallItems = (versionChanges ?? []).flatMap<WallItem>(
      (versionChange) => {
        const author = usersMap[versionChange.authorId];
        const history = post.histories.find(
          (h) =>
            h.versionFrom < versionChange.version &&
            h.versionTo >= versionChange.version,
        );
        if (!history || !author) return [];

        const element = quill.root.querySelector(
          `[${VersionChangeFormat.keyName}="${versionChange.id}"]`,
        );
        return element
          ? [
              {
                versionChange,
                author,
                top: element.getBoundingClientRect().top - containerTop,
                history,
              },
            ]
          : [];
      },
    );

    const priorityIndex = newWallItems.findIndex(
      (i) => i.versionChange.id === focusedId,
    );

    for (let i = priorityIndex - 1; i >= 0; i -= 1) {
      const current = newWallItems[i];
      const next = newWallItems[i + 1];
      if (current && next) {
        current.top = Math.min(current.top, next.top - authorCardHeight);
      }
    }

    for (let i = priorityIndex + 1; i < newWallItems.length; i += 1) {
      const current = newWallItems[i];
      const prev = newWallItems[i - 1];
      if (current && prev) {
        current.top = Math.max(current.top, prev.top + authorCardHeight);
      }
    }

    setWallItems(newWallItems);
  }, [
    versionChanges,
    quill,
    usersMap,
    post?.histories,
    authorCardHeight,
    focusedId,
  ]);

  useEffect(() => {
    updateWallItems();
  }, [updateWallItems]);

  const debouncedUpdateWallItems = useDebouncedCallback(updateWallItems, 200);

  useEffect(() => {
    if (!quill) return undefined;
    const observer = new ResizeObserver(() => {
      debouncedUpdateWallItems();
    });
    observer.observe(quill.root);
    return () => {
      observer.disconnect();
    };
  }, [debouncedUpdateWallItems, quill]);

  const lastWallItemTop = wallItems.at(-1)?.top;

  return (
    <div
      className={styles.container}
      style={{
        minHeight: lastWallItemTop && lastWallItemTop + authorCardHeight,
      }}
    >
      <div className={styles.wallItems}>
        <div ref={setElement} />
        {post &&
          wallItems.map((wallItem) => {
            const isFocused = focusedId === wallItem.versionChange.id;

            return (
              <div
                key={wallItem.versionChange.id}
                className={styles.item}
                style={{ top: wallItem.top }}
              >
                <VersionChangesWallAuthorCard
                  asPopOver={isCompact ?? false}
                  focused={isFocused}
                  author={wallItem.author}
                  history={wallItem.history}
                  post={post}
                  onClick={() => onFocus(wallItem.versionChange.id)}
                  currentVersionFrom={versionFrom}
                  currentVersionTo={versionTo}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default VersionChangesWall;
